"use client";

import { useState } from "react";

export default function ContactPage() {
  return (
    <div className="bg-white">
      <Hero />
      <ContactForm />
      <ContactDetails />
      <VisitCTA />
    </div>
  );
}

function Hero() {
  return (
    <section className="border-b border-[rgba(6,55,102,0.08)] bg-[#f4f8ff] py-24">
      <div className="container space-y-6">
        <p className="font-semibold uppercase tracking-[0.3em] text-[#2380dd]">
          Contact
        </p>
        <h1 className="text-4xl font-semibold text-[color:var(--foreground)]">
          We&rsquo;re ready to guide your Ghana market launch
        </h1>
        <p className="text-lg leading-relaxed text-muted">
          Share your portfolio, timelines, or upcoming registrations. Our regulatory team coordinates a bespoke response and connects you with the right specialists within two business days.
        </p>
      </div>
    </section>
  );
}

function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    country: "",
    message: "",
    consent: false,
  });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to send message");
      }

      setStatus("success");
      setFormData({
        name: "",
        email: "",
        company: "",
        country: "",
        message: "",
        consent: false,
      });
    } catch (error) {
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "Failed to send message");
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { id, value, type } = e.target;
    const fieldName = id.replace("contact-", "");
    
    if (type === "checkbox") {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [fieldName]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [fieldName]: value }));
    }
  };

  return (
    <section className="bg-[#f4f8ff] py-20">
      <div className="container">
        <form onSubmit={handleSubmit} className="mx-auto grid w-full max-w-3xl gap-6 rounded-3xl border border-[rgba(6,55,102,0.12)] bg-white p-8 shadow-medium">
          {status === "success" && (
            <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-sm text-green-800">
              Thank you! Your message has been sent successfully. We&apos;ll get back to you within two business days.
            </div>
          )}
          
          {status === "error" && (
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-800">
              {errorMessage || "Failed to send message. Please try again."}
            </div>
          )}

          <div className="grid gap-2">
            <label className="text-sm font-medium text-[color:var(--foreground)]" htmlFor="contact-name">
              Name *
            </label>
            <input
              id="contact-name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Your full name"
              className="rounded-xl border border-[rgba(6,55,102,0.2)] bg-white/80 px-4 py-3 text-sm text-[color:var(--foreground)] shadow-soft focus:border-[#2380dd] focus:outline-none"
            />
          </div>
          <div className="mt-6 grid gap-2">
            <label className="text-sm font-medium text-[color:var(--foreground)]" htmlFor="contact-email">
              Work email *
            </label>
            <input
              id="contact-email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="contact@company.com"
              className="rounded-xl border border-[rgba(6,55,102,0.2)] bg-white/80 px-4 py-3 text-sm text-[color:var(--foreground)] shadow-soft focus:border-[#2380dd] focus:outline-none"
            />
          </div>
          <div className="mt-6 grid gap-2">
            <label className="text-sm font-medium text-[color:var(--foreground)]" htmlFor="contact-company">
              Company
            </label>
            <input
              id="contact-company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              placeholder="Company name"
              className="rounded-xl border border-[rgba(6,55,102,0.2)] bg-white/80 px-4 py-3 text-sm text-[color:var(--foreground)] shadow-soft focus:border-[#2380dd] focus:outline-none"
            />
          </div>
          <div className="mt-6 grid gap-2">
            <label className="text-sm font-medium text-[color:var(--foreground)]" htmlFor="contact-country">
              Country
            </label>
            <input
              id="contact-country"
              type="text"
              value={formData.country}
              onChange={handleChange}
              placeholder="Country"
              className="rounded-xl border border-[rgba(6,55,102,0.2)] bg-white/80 px-4 py-3 text-sm text-[color:var(--foreground)] shadow-soft focus:border-[#2380dd] focus:outline-none"
            />
          </div>
          <div className="mt-6 grid gap-2">
            <label className="text-sm font-medium text-[color:var(--foreground)]" htmlFor="contact-message">
              Message *
            </label>
            <textarea
              id="contact-message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              required
              placeholder="Share timelines, device class, or support needed"
              className="rounded-2xl border border-[rgba(6,55,102,0.2)] bg-white/80 px-4 py-3 text-sm text-[color:var(--foreground)] shadow-soft focus:border-[#2380dd] focus:outline-none"
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              id="contact-consent"
              type="checkbox"
              checked={formData.consent}
              onChange={handleChange}
              className="h-4 w-4 rounded border-[rgba(6,55,102,0.3)] text-[#063766] focus:ring-[#2380dd]"
            />
            <label className="text-sm text-muted" htmlFor="contact-consent">
              I&apos;d like access to Regnova regulatory resources
            </label>
          </div>
          <button
            type="submit"
            disabled={status === "loading"}
            className="inline-flex items-center justify-center rounded-full bg-[#063766] px-6 py-3 text-base font-medium text-white shadow-soft transition hover:bg-[#052d55] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === "loading" ? "Sending..." : "Submit inquiry"}
          </button>
        </form>
      </div>
    </section>
  );
}

function ContactDetails() {
  const items = [
    {
      label: "Company address",
      value: "U.P.O. Box 379, KNUST, Kumasi, Ghana",
    },
    { label: "Phone", value: "+233 (0) 000 000 000" },
    { label: "Email", value: "admin@regnovagh.com" },
    {
      label: "Office hours",
      value: "Monday – Friday, 9am to 5pm GMT",
    },
  ];

  return (
    <section className="bg-white py-20">
      <div className="container mx-auto max-w-4xl space-y-8 text-center">
        <div className="space-y-4">
          <h2 className="text-3xl font-semibold text-[color:var(--foreground)]">
            Talk to our team
          </h2>
          <p className="text-lg leading-relaxed text-muted">
            We respond within two business days to align on scope, confirm regulatory pathways, and outline next steps for your Ghana FDA submissions.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((item) => (
            <div key={item.label} className="rounded-3xl border border-[rgba(6,55,102,0.12)] bg-white p-6 shadow-soft">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#2380dd]">
                {item.label}
              </p>
              <p className="mt-2 text-sm text-muted">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VisitCTA() {
  return (
    <section className="border-t border-[rgba(6,55,102,0.08)] bg-white py-16">
      <div className="container rounded-3xl bg-[#063766] px-10 py-12 text-white shadow-medium">
        <h2 className="text-3xl font-semibold">Visit us in Ghana</h2>
        <p className="mt-3 max-w-xl text-white/80">
          U.P.O. Box 379, KNUST, Kumasi, Ghana
        </p>
      </div>
    </section>
  );
}

